import { z } from 'zod'
import { guidSchema } from '../../contracts'
import { bzOptional } from '../common-schemas'

// Match reasons for duplicate detection
export const JobMatchReasonSchema = z.enum(['phone', 'email', 'name', 'address', 'openJob'])
export type JobMatchReason = z.infer<typeof JobMatchReasonSchema>

// Contact info for duplicate checking (subset of full contact)
export const JobDuplicateContactInfoSchema = z.object({
  firstName: bzOptional(z.string()),
  lastName: bzOptional(z.string()),
  phoneNumber: bzOptional(z.string()),
  emailAddress: bzOptional(z.string()),
})
export type JobDuplicateContactInfo = z.infer<typeof JobDuplicateContactInfoSchema>

// Location info for duplicate checking (subset of full location)
export const JobDuplicateLocationInfoSchema = z.object({
  addressLine1: z.string(),
  addressLine2: bzOptional(z.string()),
  city: bzOptional(z.string()),
  stateAbbreviation: z.string(),
  zipCode: z.string(),
})
export type JobDuplicateLocationInfo = z.infer<typeof JobDuplicateLocationInfoSchema>

// Request schema for checking duplicates
export const CheckJobDuplicatesRequestSchema = z.object({
  companyGuid: guidSchema,
  accountGuid: guidSchema,
  contactInfo: bzOptional(JobDuplicateContactInfoSchema),
  locationInfo: bzOptional(JobDuplicateLocationInfoSchema),
})
export type CheckJobDuplicatesRequest = z.infer<typeof CheckJobDuplicatesRequestSchema>

// Duplicate contact preview
export const DuplicateContactPreviewSchema = z.object({
  contactGuid: guidSchema,
  firstName: bzOptional(z.string()),
  lastName: bzOptional(z.string()),
  phoneNumber: bzOptional(z.string()),
  emailAddress: bzOptional(z.string()),
  matchReasons: z.array(JobMatchReasonSchema),
})
export type DuplicateContactPreview = z.infer<typeof DuplicateContactPreviewSchema>

// Duplicate location preview
export const DuplicateLocationPreviewSchema = z.object({
  locationGuid: guidSchema,
  formattedAddress: z.string(),
  matchReasons: z.array(JobMatchReasonSchema),
})
export type DuplicateLocationPreview = z.infer<typeof DuplicateLocationPreviewSchema>

// Open job preview
export const OpenJobPreviewSchema = z.object({
  jobGuid: guidSchema,
  jobNumber: z.string(),
  jobType: bzOptional(z.string()),
  jobStatus: bzOptional(z.string()),
  createdAt: z.string(),
  technicianName: bzOptional(z.string()),
  matchReasons: z.array(JobMatchReasonSchema),
})
export type OpenJobPreview = z.infer<typeof OpenJobPreviewSchema>

// Response schema for duplicate check
export const CheckJobDuplicatesResponseSchema = z.object({
  duplicateContacts: z.array(DuplicateContactPreviewSchema),
  duplicateLocations: z.array(DuplicateLocationPreviewSchema),
  openJobs: z.array(OpenJobPreviewSchema),
  hasOpenJobs: z.boolean(),
  hasDuplicates: z.boolean(),
  totalDuplicatesFound: z.number(),
})
export type CheckJobDuplicatesResponse = z.infer<typeof CheckJobDuplicatesResponseSchema>
